import { logWarn } from '@ptek/client-logger';
import { RecordingMode } from '../types/recorder';
import { ContextData } from '../types/widget';

const USER_SETTINGS_PREFERENCES_KEY_PREFIX = 'ptek_user_settings_preferences';

export type FontSizePreference = 'small' | 'medium' | 'large';

/**
 * Represents the selection state for a context item.
 * For items with children: { childKey: true } for each selected child
 * For items without children: { selected: true }
 */
export interface ContextSelectionState {
  selected?: boolean;
  [childKey: string]: boolean | undefined;
}

export interface UserSettingsPreferences {
  aiWarningDismissed?: boolean;
  hasSeenConfetti?: boolean;
  patientConsent?: boolean;
  fontSizePreference?: FontSizePreference;
  lastTemplateSelected?: string;
  microphoneDeviceId?: string;
  lastRecordingMode?: RecordingMode;
  telehealthInstructionsDismissed?: boolean;
  selectedDocumentFields?: string[];
  /** Persisted context selections for items with persistPreviousSelection: true */
  lastContextSelections?: Record<string, ContextSelectionState>;
  /** IDs of "new" templates the user has already seen, so we only auto-select once */
  seenNewTemplateIds?: string[];
}

function getUserSettingsStorageKey(userId?: string): string {
  if (!userId) {
    return `${USER_SETTINGS_PREFERENCES_KEY_PREFIX}_anonymous`;
  }
  return `${USER_SETTINGS_PREFERENCES_KEY_PREFIX}_${userId}`;
}

export function loadUserSettingsPreferences(
  userId?: string
): UserSettingsPreferences {
  if (typeof window === 'undefined' || !window.localStorage) {
    return {};
  }

  try {
    const storageKey = getUserSettingsStorageKey(userId);
    const stored = window.localStorage.getItem(storageKey);

    if (!stored) {
      return {};
    }

    const preferences: UserSettingsPreferences = JSON.parse(stored);
    return preferences || {};
  } catch (error) {
    logWarn(
      'userPreferences',
      'Failed to load user settings preferences:',
      error
    );
    return {};
  }
}

export function saveUserSettingsPreferences(
  userId: string | undefined,
  preferences: UserSettingsPreferences
): boolean {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }

  try {
    const storageKey = getUserSettingsStorageKey(userId);
    const currentPreferences = loadUserSettingsPreferences(userId);

    window.localStorage.setItem(
      storageKey,
      JSON.stringify({ ...currentPreferences, ...preferences })
    );
    return true;
  } catch (error) {
    logWarn(
      'userPreferences',
      'Failed to save user settings preferences:',
      error
    );
    return false;
  }
}

/**
 * Serialize context selections for items with persistPreviousSelection: true.
 * Only stores true values in flat structure:
 * - Items with children: { parentKey: { childKey: true } }
 * - Items without children: { parentKey: { selected: true } }
 */
export function serializePersistableContextSelections(
  contextData: ContextData
): Record<string, ContextSelectionState> {
  const map: Record<string, ContextSelectionState> = {};

  for (const item of contextData) {
    if (item.persistPreviousSelection) {
      const itemState: ContextSelectionState = {};

      if (item.children && item.children.length > 0) {
        for (const child of item.children) {
          if (child.selected === true) {
            itemState[String(child.key)] = true;
          }
        }
      } else {
        if (item.selected === true) {
          itemState.selected = true;
        }
      }

      map[String(item.key)] = itemState;
    }
  }

  return map;
}
